<template>
  <LabelValue class="mb-4" :label="t('Reviews.agency.edit.industries.label').toString()" show-icon>
    <template #hint>
      <span v-html="t('Reviews.agency.edit.industries.hint')" />
    </template>
    <div class="mt-6 grid grid-cols-3 gap-6">
      <CircleSpinner v-if="loading" class="justify-self-center" />
      <div v-else class="mt-2">
        <div
          v-for="(industry, index) in formData.industries"
          :key="`${industry._vid}-${formData.industries.length}`"
          class="relative mb-2"
          :class="{ hidden: industry._destroy }"
        >
          <SortBtnGroup
            class="absolute -top-2 right-0"
            :move-up-disabled="index === 0"
            :move-down-disabled="index === formData.industries.length - 1"
            @destroy="destroy(index)"
            @move-up="moveUp(index)"
            @move-down="moveDown(index)"
          />
          <IndustryInput
            v-model="formData.industries[index]"
            :options="availableIndustries"
            :selected-industries="getSelectedIndustries(index)"
          />
        </div>
        <div v-if="noIndustries" class="relative mb-2">
          <SortBtnGroup class="absolute -top-2 right-0" move-up-disabled move-down-disabled />
          <FieldWrapper for="industry-add-select" :label="t('Reviews.agency.edit.industry.label').toString()">
            <template #default="{ id }">
              <SelectField
                :id="id"
                :placeholder="t('Reviews.agency.edit.industry.placeholder').toString()"
                @update:model-value="addIndustry"
              >
                <option v-for="industry in availableIndustries" :key="industry.id" :value="industry.id">{{ industry.name }}</option>
              </SelectField>
            </template>
          </FieldWrapper>
        </div>
        <button
          v-if="formData.industries.length < INDUSTRIES_LIMIT"
          class="btn-primary-color-s float-right mb-4"
          data-testid="agency-add-industry-button"
          @click.prevent="addIndustry()"
        >
          <CirclePlusIcon class="mr-2" />
          {{ t('Reviews.agency.edit.industries.actions.addIndustry', formData.industries.length + 1) }}
        </button>
      </div>
      <PreviewBox class="col-span-2" :label="t('Reviews.agency.view.industriesAndServices.industries.label')">
        <ul v-if="formData.industries.length > 0" class="list-['-_'] pl-2 text-body-m">
          <li v-for="industry in selectedIndustries" :key="industry.id" class="pb-3.5">
            {{ industry.name }}
          </li>
        </ul>
      </PreviewBox>
    </div>
  </LabelValue>
</template>
<script setup lang="ts">
import { isDefined } from '@ramp106/omrjs-common'
import { CircleSpinner, FieldWrapper, SelectField } from '@ramp106/omrjs-core-ui'
import { CirclePlusIcon } from '@ramp106/omrjs-core-ui/icons'
import { useVuelidate } from '@vuelidate/core'
import { v4 as uuidv4 } from 'uuid'
import { computed, reactive } from 'vue'
import { useI18n } from 'vue-i18n'

import PreviewBox from '@/components/ReviewsManagement/PreviewBox.vue'
import { usePositionedArray } from '@/composables/usePositionedArray'
import { useReviewsAgencyOptionsQuery } from '@/gql/reviews'
import LabelValue from '@/ui/LabelValue.vue'
import SortBtnGroup from '@/ui/SortBtnGroup.vue'

import IndustryInput from './IndustryInput.vue'
import type { IndustryFormData } from './types'

const INDUSTRIES_LIMIT = 10

const props = defineProps<{
  industries: IndustryFormData[]
}>()

const { t } = useI18n()

const { result: agencyOptions, loading } = useReviewsAgencyOptionsQuery({
  clientId: 'reviews',
})

const availableIndustries = computed(() => {
  if (!agencyOptions.value?.manage?.options.industries) {
    return []
  }

  return agencyOptions.value.manage.options.industries
})

const noIndustries = computed(() => {
  return formData.industries.filter((i) => !i._destroy).length === 0
})

const formData = reactive<{ industries: IndustryFormData[] }>({
  industries: props.industries,
})

const rules = computed(() => ({
  industries: {},
}))

useVuelidate(rules, formData)

const selectedIndustries = computed(() =>
  formData.industries
    .filter((i) => !i._destroy)
    .map((i) => availableIndustries.value.find(({ id }) => id === i.industryId))
    .filter(isDefined),
)

const { create, destroy, moveUp, moveDown } = usePositionedArray(formData.industries, 'industryId')

function addIndustry(industryId: string = '') {
  create({ industryId, _vid: uuidv4() })
}

function getSelectedIndustries(excludeIndex: number) {
  return formData.industries.filter((i, index) => index !== excludeIndex && !i._destroy).map((i) => i.industryId)
}
</script>
